
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { TypingMode, TypingStats, HistoryEntry, WordStatus, Language } from '../types';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Scatter, ComposedChart } from 'recharts';
import { ChevronRight, RotateCcw, Loader2 } from 'lucide-react';
import { generateQuote } from '../services/geminiService';

const WORD_LISTS: Record<Language, string[]> = {
  [Language.EN]: "the be to of and a in that have i it for not on with he as you do at this but his by from they we say her she or an will my one all would there their what so up out if about who get which go me when make can like time no just him know take people into year your good some could them see other than then now look only come its over think also back after use two how our work first well even new want because any these give day most us".split(' '),
  [Language.RU]: "и в во не на я быть он с что а по это она этот весь мы они из за тот который свой один как так его для или если когда даже другой по свой что через после над под при от к перед".split(' '),
  [Language.KZ]: "бірақ және үшін мен ол бұл маған саған қалай неге қашан қайда жақсы жаман үлкен кіші бар жоқ емес еді болды болады ал кейін дейін соң туралы арқылы осы сол анау мынау".split(' ')
};

interface Props {
  mode: TypingMode;
  config: number;
  language: Language;
  focusMode: boolean;
  onFinish: (stats: TypingStats, history: HistoryEntry[]) => void;
  onStartTyping: () => void;
}

export const TypingEngine: React.FC<Props> = ({ mode, config, language, focusMode, onFinish, onStartTyping }) => {
  // --- State ---
  const [words, setWords] = useState<string[]>([]);
  const [input, setInput] = useState('');
  const [isFinished, setIsFinished] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [wordStatuses, setWordStatuses] = useState<WordStatus[]>([]);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  
  // UI Stats
  const [liveWpm, setLiveWpm] = useState(0);
  const [liveAccuracy, setLiveAccuracy] = useState(100);
  const [timeLeft, setTimeLeft] = useState(mode === TypingMode.TIME ? config : 0);
  
  // Frozen Stats for Result Screen (Fixes jitter)
  const [finalResultStats, setFinalResultStats] = useState<TypingStats | null>(null);

  // --- Refs ---
  const startTimeRef = useRef<number | null>(null);
  const timerIntervalRef = useRef<number | null>(null);
  const historyRef = useRef<HistoryEntry[]>([]);
  const statsRef = useRef({
    committedCorrectChars: 0, // Characters in fully completed words
    committedTypedChars: 0,
    incorrectChars: 0,
    extraChars: 0,
    missedChars: 0,
    secondErrors: 0
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const requestCounterRef = useRef(0);

  // --- Helpers ---
  const calculateWPM = (chars: number, ms: number) => {
    const seconds = Math.max(ms / 1000, 0.5); 
    return Math.round((chars / 5) / (seconds / 60));
  };

  // Helper to get total correct chars including current input buffer
  const getCurrentCorrectChars = (currentInput: string) => {
    const currentWord = words[currentWordIndex] || "";
    let correctInCurrent = 0;
    for (let i = 0; i < Math.min(currentInput.length, currentWord.length); i++) {
        if (currentInput[i] === currentWord[i]) correctInCurrent++;
    }
    return statsRef.current.committedCorrectChars + correctInCurrent;
  };

  const getCurrentTotalTyped = (currentInput: string) => {
      return statsRef.current.committedTypedChars + currentInput.length;
  };

  const getStatsSnapshot = (durationMs: number): TypingStats => {
    const correctChars = getCurrentCorrectChars(input);
    const totalTyped = getCurrentTotalTyped(input);
    
    const wpm = calculateWPM(correctChars, durationMs);
    const rawWpm = calculateWPM(totalTyped, durationMs);
    const accuracy = totalTyped > 0 ? Math.round((correctChars / totalTyped) * 100) : 0;
    const s = statsRef.current;

    return {
      wpm,
      rawWpm,
      accuracy,
      errors: s.incorrectChars + s.extraChars + s.missedChars,
      characters: {
        correct: correctChars,
        incorrect: s.incorrectChars,
        extra: s.extraChars,
        missed: s.missedChars
      },
      consistency: 85,
      time: Math.round(durationMs / 1000)
    };
  };

  // --- Initialization ---
  const initWords = useCallback(async () => {
    const requestId = ++requestCounterRef.current;
    setIsLoading(true);
    setIsFinished(false);
    setFinalResultStats(null);
    
    statsRef.current = { committedCorrectChars: 0, committedTypedChars: 0, incorrectChars: 0, extraChars: 0, missedChars: 0, secondErrors: 0 };
    historyRef.current = [{ second: 0, wpm: 0, raw: 0, errors: 0 }];
    startTimeRef.current = null;
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    
    setLiveWpm(0);
    setLiveAccuracy(100);
    setTimeLeft(mode === TypingMode.TIME ? config : 0);
    setInput('');
    setCurrentWordIndex(0);

    let textArray: string[] = [];

    try {
      if (mode === TypingMode.QUOTE) {
        const quoteLength = config === 0 ? 'short' : config === 1 ? 'medium' : 'long';
        const text = await generateQuote(language, quoteLength);
        if (requestCounterRef.current !== requestId) return;
        textArray = text.split(/\s+/).filter(w => w.length > 0);
      } else {
        const list = WORD_LISTS[language];
        textArray = Array.from({ length: mode === TypingMode.WORDS ? config : 100 }, () => 
          list[Math.floor(Math.random() * list.length)]
        );
      }

      if (requestCounterRef.current === requestId) {
        setWords(textArray);
        setWordStatuses(textArray.map(w => ({
          word: w,
          status: 'pending',
          charStatuses: Array(w.length).fill('pending')
        })));
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (requestCounterRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [mode, config, language]);

  useEffect(() => { initWords(); }, [initWords]);

  // --- Timer ---
  const startTimer = () => {
    if (startTimeRef.current) return;
    
    startTimeRef.current = Date.now();
    onStartTyping();

    timerIntervalRef.current = window.setInterval(() => {
      if (!startTimeRef.current) return;
      const now = Date.now();
      const elapsedMs = now - startTimeRef.current;
      const elapsedSec = Math.floor(elapsedMs / 1000);

      // Using input from ref implicitly by not passing it, but input state is stale in interval
      // So we rely on committed stats mostly, or we'd need a ref for current input.
      // For simplicity/perf in interval: we use committed chars + approximation or ignore incomplete word for graph
      // To fix graph accuracy: let's use committed stats only for graph to avoid fluctuating wildly
      const currentWpm = calculateWPM(statsRef.current.committedCorrectChars, elapsedMs);
      const currentRaw = calculateWPM(statsRef.current.committedTypedChars, elapsedMs);
      
      setLiveWpm(currentWpm); // Live UI updates
      
      if (mode === TypingMode.TIME) {
        const remaining = Math.max(0, config - elapsedSec);
        setTimeLeft(remaining);
        if (remaining <= 0) {
          finishTest();
          return;
        }
      }

      const newEntry: HistoryEntry = {
        second: elapsedSec,
        wpm: currentWpm,
        raw: currentRaw,
        errors: statsRef.current.secondErrors
      };
      
      if (historyRef.current[historyRef.current.length - 1]?.second !== elapsedSec) {
        historyRef.current.push(newEntry);
      }
      
      statsRef.current.secondErrors = 0;
    }, 1000);
  };

  const finishTest = () => {
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    if (!startTimeRef.current) return;

    const endTime = Date.now();
    const durationMs = endTime - startTimeRef.current;
    
    // Calculate final stats ONCE and freeze them
    const stats = getStatsSnapshot(durationMs);
    setFinalResultStats(stats);
    
    const finalEntry: HistoryEntry = {
      second: Math.round(durationMs / 1000),
      wpm: stats.wpm,
      raw: stats.rawWpm,
      errors: statsRef.current.secondErrors
    };
    
    if (historyRef.current[historyRef.current.length - 1]?.second !== finalEntry.second) {
        historyRef.current.push(finalEntry);
    }

    setIsFinished(true);
    onFinish(stats, [...historyRef.current]);
  };

  // --- Input Handler ---
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (isFinished || isLoading) return;

    if (!startTimeRef.current) startTimer();

    // Space: Word Complete
    if (val.endsWith(' ')) {
      const typedWord = val.trim();
      const currentWord = words[currentWordIndex];
      
      // Update Committed Stats
      statsRef.current.committedTypedChars += typedWord.length + 1; // +1 for space
      
      if (typedWord === currentWord) {
        statsRef.current.committedCorrectChars += currentWord.length + 1;
      } else {
        // Simple error counting for stats
        let correctInWord = 0;
        for(let i=0; i<Math.min(typedWord.length, currentWord.length); i++) {
           if(typedWord[i] === currentWord[i]) correctInWord++;
           else statsRef.current.incorrectChars++;
        }
        statsRef.current.missedChars += Math.max(0, currentWord.length - typedWord.length);
        statsRef.current.extraChars += Math.max(0, typedWord.length - currentWord.length);
      }

      setWordStatuses(prev => {
        const next = [...prev];
        next[currentWordIndex].status = typedWord === currentWord ? 'correct' : 'incorrect';
        return next;
      });

      setInput('');
      const nextIndex = currentWordIndex + 1;
      setCurrentWordIndex(nextIndex);

      if (nextIndex >= words.length || (mode === TypingMode.WORDS && nextIndex >= config)) {
        finishTest();
      }
      return;
    }

    // Typing Current Word
    const currentWord = words[currentWordIndex];
    const isBackspace = val.length < input.length;

    if (!isBackspace && val.length > 0) {
      const charIndex = val.length - 1;
      const char = val[charIndex];
      const expected = currentWord[charIndex];
      
      // We don't increment committedTypedChars here, handled by snapshot
      if (char !== expected) {
        statsRef.current.secondErrors++;
      }
    }

    // Update Visuals (This logic relies on CSS classes existing!)
    setWordStatuses(prev => {
      const next = [...prev];
      if (!next[currentWordIndex]) return prev;
      const charStatuses = [...next[currentWordIndex].charStatuses];
      for (let i = 0; i < currentWord.length; i++) {
         if (i < val.length) {
           charStatuses[i] = val[i] === currentWord[i] ? 'correct' : 'incorrect';
         } else {
           charStatuses[i] = 'pending';
         }
      }
      next[currentWordIndex].charStatuses = charStatuses;
      return next;
    });

    setInput(val);
    
    // Immediate Live WPM update (better feel)
    if (startTimeRef.current) {
        const ms = Date.now() - startTimeRef.current;
        const correct = getCurrentCorrectChars(val);
        setLiveWpm(calculateWPM(correct, ms));
    }
  };

  // --- Hotkeys ---
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab' || e.key === 'Enter') {
         if (isFinished) {
            e.preventDefault();
            initWords();
         }
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [isFinished, initWords]);

  // --- Render ---

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[300px] space-y-4 animate-fadeIn">
        <Loader2 size={48} className="text-main animate-spin" />
        <span className="text-sub uppercase tracking-widest text-xs">Generating content...</span>
      </div>
    );
  }

  if (isFinished && finalResultStats) {
    const stats = finalResultStats;
    const chartData = historyRef.current;

    return (
      <div className="w-full max-w-5xl animate-fadeIn py-10">
        <div className="flex flex-col md:flex-row gap-10 mb-12">
          <div className="flex flex-col space-y-8 min-w-[140px]">
            <div>
              <div className="text-sub uppercase text-2xl font-bold leading-tight">wpm</div>
              <div className="text-main text-7xl font-bold leading-none">{stats.wpm}</div>
            </div>
            <div>
              <div className="text-sub uppercase text-2xl font-bold leading-tight">acc</div>
              <div className="text-main text-7xl font-bold leading-none">{stats.accuracy}%</div>
            </div>
          </div>

          <div className="flex-grow h-[300px] relative bg-sub/5 rounded-xl border border-sub/10 p-4">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 5, right: 5, bottom: 5, left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2c2e31" vertical={false} />
                <XAxis dataKey="second" stroke="#646669" tick={{fontSize: 10}} axisLine={false} tickLine={false} />
                <YAxis stroke="#646669" tick={{fontSize: 10}} axisLine={false} tickLine={false} domain={[0, 'auto']} allowDecimals={false} />
                <Tooltip 
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px', color: '#d1d0c5' }} 
                    itemStyle={{ fontSize: '12px' }}
                />
                <Line type="monotone" dataKey="wpm" stroke="var(--main-color)" strokeWidth={3} dot={false} isAnimationActive={true} animationDuration={1000} />
                <Line type="monotone" dataKey="raw" stroke="#666" strokeWidth={2} dot={false} strokeDasharray="4 4" isAnimationActive={true} animationDuration={1000} />
                {chartData.map((h, i) => h.errors > 0 && (
                   <Scatter key={i} data={[{second: h.second, wpm: h.wpm}]} fill="#ca4754" shape="cross" />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-8 mb-16 px-4">
          <div>
            <div className="text-sub uppercase text-xs font-bold mb-1">Type</div>
            <div className="text-main text-lg font-bold capitalize">{mode} {mode !== TypingMode.QUOTE && config}</div>
            <div className="text-main text-sm capitalize">{language}</div>
          </div>
          <div>
            <div className="text-sub uppercase text-xs font-bold mb-1">Raw</div>
            <div className="text-main text-4xl font-bold">{stats.rawWpm}</div>
          </div>
          <div>
            <div className="text-sub uppercase text-xs font-bold mb-1">Chars</div>
            <div className="text-main text-2xl font-bold font-mono tracking-tight">
              {stats.characters.correct}/{stats.characters.incorrect}/{stats.characters.extra}/{stats.characters.missed}
            </div>
          </div>
          <div>
            <div className="text-sub uppercase text-xs font-bold mb-1">Consistency</div>
            <div className="text-main text-4xl font-bold">{stats.consistency}%</div>
          </div>
          <div>
            <div className="text-sub uppercase text-xs font-bold mb-1">Time</div>
            <div className="text-main text-4xl font-bold">{stats.time}s</div>
          </div>
        </div>

        <div className="flex flex-col items-center space-y-8">
            <div className="flex justify-center space-x-12 text-sub">
            <button onClick={initWords} className="hover:text-text transition-colors flex flex-col items-center group">
                <ChevronRight size={24} className="group-hover:translate-x-1 transition-transform" />
                <span className="text-[10px] uppercase mt-2 tracking-widest">Next</span>
            </button>
            <button onClick={initWords} className="hover:text-text transition-colors flex flex-col items-center group">
                <RotateCcw size={24} className="group-hover:-rotate-180 transition-transform duration-500" />
                <span className="text-[10px] uppercase mt-2 tracking-widest">Restart</span>
            </button>
            </div>
            
            <div className="flex items-center space-x-6 text-[11px] text-sub uppercase tracking-widest font-mono opacity-50">
                <div className="flex items-center space-x-2">
                    <kbd className="bg-sub/20 px-2 py-1 rounded text-text border border-sub/30 shadow-sm">tab</kbd>
                    <span>restart</span>
                </div>
                <div className="flex items-center space-x-2">
                    <kbd className="bg-sub/20 px-2 py-1 rounded text-text border border-sub/30 shadow-sm">enter</kbd>
                    <span>next</span>
                </div>
            </div>
        </div>
      </div>
    );
  }

  // Active Typing View
  return (
    <div className="w-full max-w-5xl">
      <div className={`flex justify-between items-center mb-12 text-main font-bold text-2xl h-8 transition-opacity duration-500 ${startTimeRef.current && focusMode ? 'opacity-0' : 'opacity-100'}`}>
        <div className="font-mono">
          {mode === TypingMode.TIME ? timeLeft : mode === TypingMode.QUOTE ? 'quote' : `${currentWordIndex}/${config}`}
        </div>
        <div className="flex space-x-8 text-sm uppercase text-sub">
          <div className="flex flex-col items-end">
             <span>WPM</span>
             <span className="text-text font-mono text-xl">{liveWpm}</span>
          </div>
        </div>
      </div>

      <div className="relative cursor-text typing-area outline-none min-h-[150px]" onClick={() => inputRef.current?.focus()}>
        <input 
          ref={inputRef} type="text" className="absolute opacity-0 pointer-events-none" 
          value={input} onChange={handleInput} autoFocus autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
        />
        <div className="flex flex-wrap gap-x-[0.3em] gap-y-[0.3em] select-none leading-relaxed transition-all">
          {wordStatuses.map((ws, wIdx) => (
            <div key={wIdx} className={`relative transition-colors duration-200 ${wIdx === currentWordIndex ? 'bg-sub/10 rounded px-1 -ml-1' : ''}`}>
              {ws.word.split('').map((char, cIdx) => {
                let colorClass = 'text-sub opacity-50'; // Default untyped
                if (wIdx < currentWordIndex) {
                   // Previous words
                   if (ws.status === 'incorrect') colorClass = 'text-error border-b-2 border-error';
                   else colorClass = 'text-text';
                } else if (wIdx === currentWordIndex) {
                  // Current word
                  if (cIdx < input.length) {
                    colorClass = input[cIdx] === char ? 'text-text' : 'text-error';
                  } else if (cIdx === input.length) {
                    colorClass = 'text-sub opacity-50 border-l-2 border-main animate-pulse pl-[1px]'; // Caret
                  }
                }
                return <span key={cIdx} className={colorClass}>{char}</span>;
              })}
              {wIdx === currentWordIndex && input.length > ws.word.length && (
                <span className="text-error opacity-70">{input.substring(ws.word.length)}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className={`mt-20 flex flex-col items-center space-y-6 transition-opacity duration-500 ${startTimeRef.current && focusMode ? 'opacity-0' : 'opacity-100'}`}>
        <button onClick={initWords} className="p-3 text-sub hover:text-text transition-colors rounded-full hover:bg-sub/10 group">
          <RotateCcw size={24} className="group-hover:-rotate-180 transition-transform duration-500" />
        </button>
      </div>
    </div>
  );
};
